import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'

import type {
    BrandDetail,
    BrandHighlight,
    BrandProduct,
    BrandReference,
    BrandSummary,
    InterestOptions,
    LayoutData,
    PageSection
} from '@/types'

import Link from 'next/link'
import { ArrowDown, ChevronRight } from 'lucide-react'
import { cms } from '@/lib/cms'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'

import {
    getBrandBySlug,
    getBrandHighlights,
    getBrandProducts,
    getBrandReferences,
    getBrands,
    getInterestOptions,
    getLayoutData,
    getSections,
    getServices
} from '@/lib/content'

import Reveal from '@/components/landing/reveal'
import BrandCard from '@/components/landing/brand-card'
import RichText from '@/components/generals/rich-text'
import ContactSection from '@/components/landing/contact-section'

type BrandPageProps = LayoutData & {
    brand: BrandDetail
    products: BrandProduct[]
    highlights: BrandHighlight[]
    references: BrandReference[]
    others: BrandSummary[]
    contact: PageSection | null
    brandsSection: PageSection | null
    interests: InterestOptions
}

type Group<T> = { key: string, label: string | null, first: T, items: T[] }

// Keeps the CMS order while gathering rows that share a group label.
const groupBy = <T extends { id: string }>(rows: T[], label: (row: T) => string | null): Group<T>[] => {
    const groups: Group<T>[] = []

    for (const row of rows) {
        const value = label(row)
        const key = value?.trim().toLowerCase() || '__none__'
        const group = groups.find(item => item.key === key)

        if (group) group.items.push(row)
        else groups.push({ key, label: value, first: row, items: [row] })
    }

    return groups
}

const BrandPage: FC<BrandPageProps> = ({
    settings,
    brands,
    brand,
    products,
    highlights,
    references,
    others,
    contact,
    brandsSection,
    interests
}): ReactNode => {
    const productGroups = groupBy(products, row => row.group_name)
    const referenceGroups = groupBy(references, row => row.kind)

    return (
        <Layout settings={settings} brands={brands}>
            <Seo
                title={brand.name}
                siteName={settings?.site_name}
                description={brand.summary ?? brand.tagline ?? undefined}
                ogImage={brand.cover_image ?? undefined}
            />

            <section aria-labelledby='brand-heading' className='bg-stone'>
                <div className='grid lg:min-h-[88svh] lg:grid-cols-2'>
                    <div className='flex flex-col justify-center px-5 pb-16 pt-28 sm:px-8 lg:py-32 lg:pl-[max(2rem,calc((100vw_-_80rem)/2_+_2rem))] lg:pr-16'>
                        <nav aria-label='Breadcrumb'>
                            <ol className='flex flex-wrap items-center gap-2 text-sm text-muted-foreground'>
                                <li>
                                    <Link href='/' className='hover:text-foreground focus-visible:underline focus-visible:outline-none'>Home</Link>
                                </li>
                                <li aria-hidden='true'><ChevronRight className='size-3.5' /></li>
                                <li>
                                    <Link href='/#brands' className='hover:text-foreground focus-visible:underline focus-visible:outline-none'>Brands</Link>
                                </li>
                                <li aria-hidden='true'><ChevronRight className='size-3.5' /></li>
                                <li aria-current='page' className='text-foreground'>
                                    <span data-mdlfy-cms={cms('brands', brand.id, 'name')}>{brand.name}</span>
                                </li>
                            </ol>
                        </nav>

                        <Reveal className='mt-14'>
                            {brand.category && (
                                <p className='eyebrow flex items-center gap-4 text-muted-foreground'>
                                    <span aria-hidden='true' className='h-px w-10 bg-foreground/30' />
                                    <span data-mdlfy-cms={cms('brands', brand.id, 'category')}>{brand.category}</span>
                                </p>
                            )}

                            <h1 id='brand-heading' className='display mt-6 text-5xl sm:text-6xl xl:text-7xl'>
                                <span data-mdlfy-cms={cms('brands', brand.id, 'name')}>{brand.name}</span>
                            </h1>

                            {(brand.origin || brand.founded) && (
                                <dl className='mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm'>
                                    {brand.origin && (
                                        <div className='flex gap-2'>
                                            <dt className='text-muted-foreground'>Origin</dt>
                                            <dd className='text-clay'>
                                                <span data-mdlfy-cms={cms('brands', brand.id, 'origin')}>{brand.origin}</span>
                                            </dd>
                                        </div>
                                    )}

                                    {brand.founded && (
                                        <div className='flex gap-2'>
                                            <dt className='text-muted-foreground'>Founded</dt>
                                            <dd className='text-clay'>
                                                <span data-mdlfy-cms={cms('brands', brand.id, 'founded')}>{brand.founded}</span>
                                            </dd>
                                        </div>
                                    )}
                                </dl>
                            )}

                            {brand.tagline && (
                                <p className='mt-8 max-w-xl text-lg leading-relaxed text-foreground/75 sm:text-xl'>
                                    <span data-mdlfy-cms={cms('brands', brand.id, 'tagline')}>{brand.tagline}</span>
                                </p>
                            )}

                            <a
                                href='#inquire'
                                className='mt-10 inline-flex items-center gap-3 bg-primary px-7 py-4 text-primary-foreground transition-colors hover:bg-ocean focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring'
                            >
                                <span className='eyebrow'>Inquire about this brand</span>
                                <ArrowDown className='size-4' aria-hidden='true' />
                            </a>
                        </Reveal>
                    </div>

                    <div className='relative min-h-[55svh] overflow-hidden bg-sand lg:min-h-full'>
                        {brand.cover_image && (
                            <img
                                src={brand.cover_image}
                                alt={brand.cover_image_alt ?? ''}
                                fetchPriority='high'
                                className='absolute inset-0 size-full object-cover'
                            />
                        )}

                        {brand.logo && (
                            <div className='absolute bottom-6 left-6 bg-background/95 px-5 py-4'>
                                <img src={brand.logo} alt={brand.logo_alt ?? brand.name} className='h-10 w-auto max-w-48 object-contain' />
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {highlights.length > 0 && (
                <section aria-label='Key figures' className='border-b border-border bg-background'>
                    <ul className='mx-auto grid max-w-7xl gap-px bg-border sm:grid-cols-2 lg:grid-cols-4'>
                        {highlights.map((highlight, index) => (
                            <li key={highlight.id} className='bg-background px-5 py-12 sm:px-8'>
                                <Reveal delay={(index % 4) * 100}>
                                    <p className='display text-4xl text-ocean sm:text-5xl'>
                                        <span data-mdlfy-cms={cms('brand_highlights', highlight.id, 'value')}>{highlight.value}</span>
                                    </p>

                                    {highlight.label && (
                                        <p className='eyebrow mt-5 leading-relaxed text-foreground'>
                                            <span data-mdlfy-cms={cms('brand_highlights', highlight.id, 'label')}>{highlight.label}</span>
                                        </p>
                                    )}

                                    {highlight.description && (
                                        <p className='mt-3 text-sm leading-relaxed text-muted-foreground'>
                                            <span data-mdlfy-cms={cms('brand_highlights', highlight.id, 'description')}>{highlight.description}</span>
                                        </p>
                                    )}
                                </Reveal>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {brand.description && (
                <section aria-labelledby='overview-heading' className='bg-background py-24 sm:py-28'>
                    <div className='mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-20'>
                        <Reveal className='lg:col-span-4'>
                            <p className='eyebrow flex items-center gap-4 text-muted-foreground'>
                                <span aria-hidden='true' className='h-px w-10 bg-foreground/30' />
                                <span>Overview</span>
                            </p>
                            <h2 id='overview-heading' className='display mt-6 text-3xl sm:text-4xl'>
                                <span className='sr-only'>About </span>
                                <span data-mdlfy-cms={cms('brands', brand.id, 'name')}>{brand.name}</span>
                            </h2>
                        </Reveal>

                        <Reveal delay={120} className='lg:col-span-8'>
                            <RichText html={brand.description} className='max-w-3xl text-[1.05rem] leading-relaxed text-foreground/80' />
                        </Reveal>
                    </div>
                </section>
            )}

            {productGroups.length > 0 && (
                <section aria-labelledby='range-heading' className='bg-stone py-24 sm:py-28'>
                    <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                        <Reveal>
                            <p className='eyebrow flex items-center gap-4 text-muted-foreground'>
                                <span aria-hidden='true' className='h-px w-10 bg-foreground/30' />
                                <span>The range</span>
                            </p>
                            <h2 id='range-heading' className='display mt-6 text-3xl sm:text-4xl'>
                                <span>Products &amp; offering</span>
                            </h2>
                        </Reveal>

                        <div className='mt-16 space-y-16'>
                            {productGroups.map(group => (
                                <div key={group.key}>
                                    {group.label && (
                                        <h3 className='eyebrow border-b border-foreground/15 pb-4 text-clay'>
                                            <span data-mdlfy-cms={cms('brand_products', group.first.id, 'group_name')}>{group.label}</span>
                                        </h3>
                                    )}

                                    <ul className='mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                                        {group.items.map((product, index) => (
                                            <li key={product.id}>
                                                <Reveal delay={(index % 3) * 100} className='h-full'>
                                                    <article className='flex h-full flex-col bg-background'>
                                                        {product.image && (
                                                            <div className='aspect-[4/3] overflow-hidden bg-sand'>
                                                                <img src={product.image} alt={product.image_alt ?? product.name} loading='lazy' className='size-full object-cover' />
                                                            </div>
                                                        )}

                                                        <div className='flex flex-1 flex-col p-7'>
                                                            <h4 className='font-heading text-base font-medium uppercase tracking-[0.12em]'>
                                                                <span data-mdlfy-cms={cms('brand_products', product.id, 'name')}>{product.name}</span>
                                                            </h4>

                                                            {product.description && (
                                                                <p className='mt-4 text-[0.95rem] leading-relaxed text-muted-foreground'>
                                                                    <span data-mdlfy-cms={cms('brand_products', product.id, 'description')}>{product.description}</span>
                                                                </p>
                                                            )}
                                                        </div>
                                                    </article>
                                                </Reveal>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {brand.gallery.length > 0 && (
                <section aria-label='Gallery' className='bg-background py-24 sm:py-28'>
                    <ul className='mx-auto grid max-w-7xl grid-cols-2 gap-4 px-5 sm:px-8 lg:grid-cols-3'>
                        {brand.gallery.map((image, index) => (
                            <li key={`${image}-${index}`} className='aspect-square overflow-hidden bg-stone'>
                                <img src={image} alt={brand.gallery_alt[index] ?? ''} loading='lazy' className='size-full object-cover' />
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {referenceGroups.length > 0 && (
                <section aria-labelledby='references-heading' className='bg-ink py-24 text-white sm:py-28'>
                    <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                        <Reveal>
                            <p className='eyebrow flex items-center gap-4 text-white/55'>
                                <span aria-hidden='true' className='h-px w-10 bg-white/40' />
                                <span>Recognition</span>
                            </p>
                            <h2 id='references-heading' className='display mt-6 text-3xl text-white sm:text-4xl'>
                                <span>Where you will find </span>
                                <span data-mdlfy-cms={cms('brands', brand.id, 'name')}>{brand.name}</span>
                            </h2>
                        </Reveal>

                        <div className='mt-14 grid gap-14 lg:grid-cols-2'>
                            {referenceGroups.map(group => (
                                <Reveal key={group.key}>
                                    {group.label && (
                                        <h3 className='eyebrow border-b border-white/15 pb-4 text-white/55'>
                                            <span data-mdlfy-cms={cms('brand_references', group.first.id, 'kind')}>{group.label}</span>
                                        </h3>
                                    )}

                                    <ul className='mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2'>
                                        {group.items.map(reference => (
                                            <li key={reference.id}>
                                                <p className='font-heading text-[0.95rem] uppercase tracking-[0.1em] text-white'>
                                                    <span data-mdlfy-cms={cms('brand_references', reference.id, 'name')}>{reference.name}</span>
                                                </p>
                                                {reference.note && (
                                                    <p className='mt-1 text-sm text-white/55'>
                                                        <span data-mdlfy-cms={cms('brand_references', reference.id, 'note')}>{reference.note}</span>
                                                    </p>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <ContactSection
                id='inquire'
                section={contact}
                settings={settings}
                interests={interests}
                defaultInterest={brand.name}
            />

            {others.length > 0 && (
                <section aria-labelledby='more-brands-heading' className='bg-background py-24 sm:py-28'>
                    <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                        <div className='flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between'>
                            <h2 id='more-brands-heading' className='display text-3xl sm:text-4xl'>More brands</h2>
                            <Link href='/#brands' className='eyebrow text-foreground/70 underline-offset-8 hover:text-foreground hover:underline focus-visible:underline focus-visible:outline-none'>
                                View all brands
                            </Link>
                        </div>

                        <ul className='mt-14 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3'>
                            {others.map((other, index) => (
                                <li key={other.id} className={index === 2 ? 'sm:hidden lg:block' : undefined}>
                                    <BrandCard brand={other} cta={brandsSection} />
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    // Pages are generated on first visit, so a brand added in the CMS gets its
    // page automatically without publishing again.
    return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps<BrandPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!slug) return { notFound: true, revalidate: 60 }

    const brand = await getBrandBySlug(slug)

    if (!brand) return { notFound: true, revalidate: 60 }

    const [layout, products, highlights, references, sections, serviceList, allBrands] = await Promise.all([
        getLayoutData(),
        getBrandProducts(brand.id),
        getBrandHighlights(brand.id),
        getBrandReferences(brand.id),
        getSections(),
        getServices(),
        getBrands()
    ])

    const position = allBrands.findIndex(item => item.id === brand.id)
    const others = allBrands.length > 1
        ? Array.from({ length: Math.min(3, allBrands.length - 1) }, (_, offset) => allBrands[(position + offset + 1) % allBrands.length])
        : []

    return {
        props: {
            ...layout,
            brand,
            products,
            highlights,
            references,
            others,
            contact: sections.contact ?? null,
            brandsSection: sections.brands ?? null,
            interests: getInterestOptions(serviceList, layout.brands)
        },
        revalidate: 60
    }
}

export default BrandPage
